import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import useAuth from "../../hooks/useAuth";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config } from "../../Environment";
import { tryGetUserProfile } from "../../api/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoginSuccess: () => void;
  title?: string;
  description?: string;
}

export function LoginDialog({
  open,
  onOpenChange,
  onLoginSuccess,
  title,
  description,
}: LoginDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth, setUser } = useAuth();
  const retryFetch = useRetryFetch();
  const emailRef = useRef<HTMLInputElement>(null);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Focus email input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => emailRef.current?.focus(), 100);
    }
  }, [open]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginEmail || !loginPassword) {
      setLoginError(
        t("msg_error_email_and_password_required") ||
          "Email and password are required"
      );
      return;
    }

    setIsLoggingIn(true);
    setLoginError("");

    try {
      const response = await fetch(config.API_BASE + "/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
        credentials: "include",
      });

      if (!response.ok) {
        setLoginError(
          t("msg_error_email_or_password_not_correct") ||
            "Email or password is incorrect"
        );
        setIsLoggingIn(false);
        return;
      }

      const data = await response.json();
      const accessToken = data?.accessToken;

      // Set auth
      setAuth({
        email: loginEmail,
        accessToken,
        impersonatedBy: undefined,
      });

      // Fetch user profile
      const userProfile = await tryGetUserProfile(retryFetch);
      if (userProfile) {
        userProfile.roles ??= [];
        setUser(userProfile);
      }

      // Close dialog and reset
      onOpenChange(false);
      setLoginEmail("");
      setLoginPassword("");
      toast.success(t("login_successful") || "Login successful!");

      // Notify parent
      onLoginSuccess();
    } catch (error) {
      console.error("Login error:", error);
      setLoginError(t("login_failed") || "Login failed. Please try again.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {title || t("login") || "Login"}
          </DialogTitle>
          {description && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="login-dialog-email">
              {t("email_address") || "Email"}
            </Label>
            <Input
              id="login-dialog-email"
              type="email"
              placeholder="m@example.com"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              ref={emailRef}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-dialog-password">
              {t("password") || "Password"}
            </Label>
            <Input
              id="login-dialog-password"
              type="password"
              placeholder={t("enter_your_password") || "Enter your password"}
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
            />
          </div>
          {loginError && (
            <div className="p-3 text-sm text-red-600 rounded-md bg-red-50">
              {loginError}
            </div>
          )}
          <div className="flex flex-col gap-3">
            <Button type="submit" disabled={isLoggingIn} className="w-full">
              {isLoggingIn
                ? t("logging_in") || "Logging in..."
                : t("login") || "Login"}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  navigate("/register", {
                    state: {
                      from: {
                        pathname: location.pathname,
                        search: location.search,
                        hash: location.hash,
                      },
                    },
                  });
                }}
                className="text-sm text-primary hover:underline"
              >
                {t("dont_have_an_account") || "Don't have an account?"}{" "}
                {t("sign_up") || "Sign up"}
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
